/* global globalThis */
import {PHASES, WAR_PHASE_STAGES} from "../../../constants/phases";
import {GAME_TYPES} from "../../../constants/game";
import {checkIfHexBlockedByEnemyUnit} from "../checkIfHexBlockedByEnemyUnit";
import {COMMON_BUILDING_TOKENS, TOKENS} from "../../../constants/tokens";
import {calculateAvailableSupplyCells} from "../calculateAvailableSupplyCells";
import {calcFarmRemainingSupply} from "../calcFarmRemainingSupply";
import {distributeGiantsAdditionalSupply, distributeSupply, recruitUnit} from "../../../redux/actions";
import {checkIfUnitHasPendingAdditionalSupply} from "../checkIfUnitHasPendingAdditionalSupply";
import {MAX_UNIT_SUPPLY} from "../../../constants/supply";
import {UNIT_TYPES} from "../../../configs/units";
import {createRandomId} from "../../ids/createRandomId";
import {ID_PREFIX_FOR_UNIT} from "../../../constants/ids";
import {checkSameTypeUnitRecruitmentLimit} from "../recruitment/checkSameTypeUnitRecruitmentLimit";
import {calcRecruitmentStageMinCoins} from "../recruitment/calcRecruitmentStageMinCoins";
import {
  completeParallelSupplyForPlayer,
  executePhaseCompletion,
  executeWarPhaseStageCompletion
} from "./handleCompleteTurn";
import {checkIfParallelSupplyPhase, getNotCompletedPlayers} from "../supply/parallelSupply";
import {isUnitInsectoid} from "../../factions/insectoids/isUnitInsectoid";
import {FACTION_INSECTOIDS_MOCK_FARM_HEX_KEY} from "../../../configs/insectoids";
import {isPlayerPolicy} from "../../factions/isPlayerPolicy";
import {POLICIES} from "../../../constants/policies";
import {
  POLICY_FANATICISM_COINS_CONVERSION,
  POLICY_FANATICISM_WIN_POINTS_CONVERSION
} from "../../../configs/policy/fanaticism";
import {
  selectBuildingsDamage,
  selectCitiesCache,
  selectCurrentPlayer,
  selectGameResults,
  selectGameType,
  selectHexes,
  selectIsGameStarted,
  selectPendingRecruitmentActions,
  selectPendingSupply,
  selectPhase,
  selectPlayers,
  selectRound,
  selectSelectedFarmsForConversion,
  selectSharedGameState,
  selectSpecialBuildings,
  selectUnits,
  selectWarPhaseStage
} from "../../../redux/reducers/sharedStateReducer/selectors";
import {isPlayerFaction} from "../../factions/isPlayerFaction";
import {checkIfPlayerHasUnitOfFaction} from "../../factions/checkIfPlayerHasUnitOfFaction";
import {FACTIONS} from "../../../constants/factions";
import {checkIfFinalRound} from "../../checkIfFinalRound";
import {GIANTS_ADDITIONAL_SUPPLY_COST} from "../../../configs/factions/giants";
import {compareRatmenUnitsForAdditionalSupply} from "../supply/compareRatmenUnitsForAdditionalSupply";
import {getGridGeometry} from "../progress/getGridGeometry";

const autoSupplyTracker = new WeakMap();

/**
 * Ключ метки авто-раздачи. Обычно это сам стор, но обертка может указать
 * вместо себя стор, который оборачивает (autoActionsTrackerKey). Так делает
 * маска перехваченных ботов (см. createControlledBotsMaskedStore): сервер
 * создает ее заново на каждый вызов, и метка на одноразовой обертке терялась
 * бы — авто-раздача повторялась после любого действия игрока, в том числе
 * сразу возвращала отмененное снабжение
 */
const getAutoSupplyTrackerKey = (store) => store.autoActionsTrackerKey || store;

/**
 * Сброс трекера авто-снабжения для конкретного стора. Нужен окружениям,
 * которые вызывают executePhaseAutoActions ТОЛЬКО в фазе снабжения (генератор
 * миссий, сценарные тесты): у них не срабатывает штатный сброс «фаза снабжения
 * закончилась» (он живет в вызовах из других фаз). Раунд в метке и так не
 * дает ей заглушить следующий раунд — сброс нужен, чтобы начать с чистого листа.
 * Поведение самой игры не меняет.
 */
export const resetAutoSupplyTracker = (store) => {
  autoSupplyTracker.delete(getAutoSupplyTrackerKey(store));
};

// Метка «авто-раздача этой фазы уже прошла» — по игрокам: в одновременной фазе
// снабжения сервер раздает всем, и метка одного-единственного игрока
// затиралась бы следующим — авто-раздача повторялась бы после каждого действия
// и возвращала отмененное снабжение (прод-регрессия p4h8gnz, теперь у всех)
const getSupplyTracker = (store) => {
  const key = getAutoSupplyTrackerKey(store);
  if (!autoSupplyTracker.has(key)) {
    autoSupplyTracker.set(key, {round: null, phase: null, playerIds: new Set()});
  }
  return autoSupplyTracker.get(key);
};

const shouldSkipSupplyAutoDistribution = (store, currentPlayer, phase, round) => {
  const tracker = getSupplyTracker(store);
  return tracker.round === round && tracker.phase === phase && tracker.playerIds.has(currentPlayer.id);
};

const markSupplyAutoDistributed = (store, currentPlayer, phase, round) => {
  const tracker = getSupplyTracker(store);
  if (tracker.round !== round || tracker.phase !== phase) {
    autoSupplyTracker.set(getAutoSupplyTrackerKey(store), {round, phase, playerIds: new Set([currentPlayer.id])});
    return;
  }
  tracker.playerIds.add(currentPlayer.id);
};

/**
 * Авто-раздача снабжения для великанов - управляется персональным тумблером игрока
 * (isAutoAdditionalSupplyOn, см. GiantsAutoSupplyToggle). Простой, полностью
 * детерминированный алгоритм в 3 фазы (мутирует plan.budget у farmPlans):
 *
 * 1. Каждому достижимому великану - 1 базовое снабжение (гарантия пайка).
 * 2. Если тумблер включен и оставшегося бюджета хватает на батч из
 *    GIANTS_ADDITIONAL_SUPPLY_COST (4) ферм, достижимых одним и тем же отрядом -
 *    крупнейший подходящий отряд (без доп. снабжения за этот ход) получает батч
 *    доп. снабжения (не растит размер - обычный бонус фазы войны). Повторяется,
 *    пока хватает бюджета на следующий подходящий отряд.
 * 3. Весь оставшийся бюджет (либо целиком, если тумблер выключен) - доп. базовое
 *    снабжение, равномерно (round robin) между отрядами.
 *
 * @param store
 * @param dryRun - true: только считает, не диспатчит (для checkIfTurnWillBeAutoCompleted)
 * @param farmPlans - [{farmHexKey, budget, targetUnits: [unit,...]}], budget мутируется
 * @param playerUnits - юниты текущего игрока (все - великаны)
 * @param pendingSupply - текущий gameState.pendingSupply (для проверки уже
 * полученного в этот ход доп. снабжения)
 * @param isAutoAdditionalSupplyOn - состояние тумблера игрока
 */
const runGiantsAutoSupply = (store, dryRun, farmPlans, playerUnits, pendingSupply, isAutoAdditionalSupplyOn, supplyActorId) => {
  const basicSupplies = [];
  const additionalBatches = [];
  // Засеваем уже розданным в этот ход базовым снабжением (см. pendingSupplyByUnit
  // в executePhaseAutoActions): повторный прогон не должен заново выдавать паек
  // отряду, который его уже получил
  const basicCountByUnit = {};
  playerUnits.forEach(unit => {
    basicCountByUnit[unit.id] = pendingSupply.filter(
      supply => supply.unitId === unit.id && !supply.additionalSupply
    ).length;
  });

  const assignBasic = (plan, unit) => {
    basicSupplies.push({farmHexKey: plan.farmHexKey, unitId: unit.id, unitHexKey: unit.hexKey});
    plan.budget -= 1;
    basicCountByUnit[unit.id] += 1;
  };

  // Фаза 1: гарантия 1 базового снабжения каждому достижимому отряду
  for (const plan of farmPlans) {
    if (plan.budget <= 0) continue;
    for (const unit of plan.targetUnits) {
      if (plan.budget <= 0) break;
      if (basicCountByUnit[unit.id] >= 1) continue;
      assignBasic(plan, unit);
    }
  }

  // Есть ли у игрока способ распределить снабжение ИНАЧЕ, чем это сделал бы
  // алгоритм - фиксируем ДО фазы 2/3 (та выбирает и расходует бюджет за игрока),
  // независимо от положения тумблера: авто-распределение ускоряет раздачу, но
  // не должно принимать финальное решение вместо игрока, если у него был выбор.
  // (a) ферма, достижимая 2+ отрядами - выбор, КОМУ отдать её снабжение
  //     (в отличие от других фракций, у великанов нет капа на базовое снабжение,
  //     который рано или поздно "стирает" эту неоднозначность насыщением)
  const hasMultiTargetFarm = farmPlans.some(plan => plan.targetUnits.length >= 2);
  // (b) после гарантированного пайка у какого-то отряда хватает достижимого
  //     бюджета на батч доп. снабжения - выбор, дать ли ему ТОЛЬКО базовое или
  //     базовое + дополнительное
  const hasAdditionalSupplyOpportunity = playerUnits.some(unit => {
    if (basicCountByUnit[unit.id] < 1) return false;
    if (checkIfUnitHasPendingAdditionalSupply(unit.id, pendingSupply)) return false;
    const reachableBudget = farmPlans
      .filter(plan => plan.budget > 0 && plan.targetUnits.some(target => target.id === unit.id))
      .reduce((sum, plan) => sum + plan.budget, 0);
    return reachableBudget >= GIANTS_ADDITIONAL_SUPPLY_COST;
  });
  const hasUnresolvedChoice = hasMultiTargetFarm || hasAdditionalSupplyOpportunity;

  // Фаза 2: доп. снабжение крупнейшему подходящему отряду, пока хватает бюджета
  if (isAutoAdditionalSupplyOn) {
    const hasAdditionalSupplyThisTurn = (unitId) => {
      return checkIfUnitHasPendingAdditionalSupply(unitId, pendingSupply)
        || additionalBatches.some(batch => batch.unitId === unitId);
    };

    let keepGoing = true;
    while (keepGoing) {
      keepGoing = false;

      // Доп. снабжение требует уже полученного в этот ход базового (см. reducer
      // DISTRIBUTE_GIANTS_ADDITIONAL_SUPPLY) - отряд без него (не достижим ни
      // одной фермой в фазе 1) пропускаем
      const eligibleUnits = playerUnits
        .filter(unit => basicCountByUnit[unit.id] >= 1 && !hasAdditionalSupplyThisTurn(unit.id))
        .sort((a, b) => b.size - a.size);

      for (const unit of eligibleUnits) {
        const reachablePlans = farmPlans.filter(
          plan => plan.budget > 0 && plan.targetUnits.some(target => target.id === unit.id)
        );
        const reachableBudget = reachablePlans.reduce((sum, plan) => sum + plan.budget, 0);
        if (reachableBudget < GIANTS_ADDITIONAL_SUPPLY_COST) continue;

        const farmHexKeys = [];
        for (const plan of reachablePlans) {
          while (plan.budget > 0 && farmHexKeys.length < GIANTS_ADDITIONAL_SUPPLY_COST) {
            farmHexKeys.push(plan.farmHexKey);
            plan.budget -= 1;
          }
          if (farmHexKeys.length >= GIANTS_ADDITIONAL_SUPPLY_COST) break;
        }

        additionalBatches.push({farmHexKeys, unitId: unit.id, unitHexKey: unit.hexKey});
        keepGoing = true;
        break;
      }
    }
  }

  // Фаза 3: остаток бюджета - доп. базовое снабжение, равномерно (round robin)
  let progress = true;
  while (progress) {
    progress = false;
    const candidates = [...playerUnits].sort((a, b) => basicCountByUnit[a.id] - basicCountByUnit[b.id]);
    for (const unit of candidates) {
      const plan = farmPlans.find(p => p.budget > 0 && p.targetUnits.some(target => target.id === unit.id));
      if (!plan) continue;
      assignBasic(plan, unit);
      progress = true;
      break;
    }
  }

  if (!dryRun) {
    for (const supply of basicSupplies) {
      store.dispatch(distributeSupply(supply.farmHexKey, supply.unitId, supply.unitHexKey, true, false, undefined, supplyActorId));
    }
    for (const batch of additionalBatches) {
      store.dispatch(distributeGiantsAdditionalSupply(batch.farmHexKeys, batch.unitId, supplyActorId));
    }
  }

  return {hasUnresolvedChoice};
};

/**
 * Сухой прогон executePhaseAutoActions: предсказывает, будет ли текущий ход
 * (фаза/этап) автозавершен исполнителем автодействий, НИЧЕГО не диспатча и не
 * трогая трекер авто-снабжения. Нужен клиенту сетевой игры, чтобы не
 * показывать оверлей «Ваш ход» (и не сбрасывать инспекцию ячейки) для хода,
 * который сервер сейчас пропустит, — событие пропуска может прийти позже
 * задержки показа. Использует ту же логику решений, что и реальный
 * исполнитель, поэтому не может разойтись с ним при изменениях правил
 */
export const checkIfTurnWillBeAutoCompleted = (store, playerId) => {
  const dryRunResult = {willComplete: false};
  // В одновременной фазе снабжения ход у каждого свой — предсказываем для
  // переданного игрока (обычно — действующего игрока клиента)
  executePhaseAutoActions(store, false, null, dryRunResult, {actingPlayerId: playerId});
  return dryRunResult.willComplete;
};

/**
 * Выполняет автоматические действия для текущего игрока в его ход.
 * В LOCAL-игре вызывается на клиенте, в сетевых играх — на сервере.
 *
 * @param {Object} store - Redux store (или store-обёртка с getState/dispatch)
 * @param {boolean} isClientExecutor - true для клиентского выполнения, false для серверного
 * @param {Object|null} hexGridRenderer - рендерер гексов (для клиента) или null (для сервера)
 * @param {Object|null} dryRunResult - режим сухого прогона (см.
 * checkIfTurnWillBeAutoCompleted): вместо диспатчей и завершения хода только
 * выставляет dryRunResult.willComplete
 * @param {Object} [options]
 * @param {string} [options.actingPlayerId] - одновременная фаза снабжения:
 * авто-действия за этого игрока. Без него сервер проходит по всем еще не
 * закончившим фазу игрокам в порядке хода
 */
export const executePhaseAutoActions = (store, isClientExecutor, hexGridRenderer = null, dryRunResult = null, options = {}) => {
  const dryRun = Boolean(dryRunResult);
  const state = store.getState();
  const gameType = selectGameType(state);

  // Защита от неправильного использования: LOCAL только на клиенте, ONLINE
  // только на сервере. Сухой прогон — исключение: он ничего не диспатчит,
  // поэтому безопасен на любой стороне
  if (!dryRun) {
    if (gameType === GAME_TYPES.LOCAL && !isClientExecutor) {
      return;
    }
    if (gameType !== GAME_TYPES.LOCAL && isClientExecutor) {
      return;
    }
  }

  const isGameStarted = selectIsGameStarted(state);
  const gameResults = selectGameResults(state);
  const phase = selectPhase(state);
  const warPhaseStage = selectWarPhaseStage(state);
  const sharedGameState = selectSharedGameState(state);
  const isParallelSupply = checkIfParallelSupplyPhase(sharedGameState);
  const actingPlayerId = options?.actingPlayerId;
  // В одновременной фазе снабжения авто-действия — за конкретного игрока, а не
  // за текущего (он в этой фазе только якорь слепка хода)
  const currentPlayer = isParallelSupply && actingPlayerId
    ? selectPlayers(state).find((player) => player.id === actingPlayerId)
    : selectCurrentPlayer(state);

  if (!isGameStarted || gameResults) {
    if (!dryRun) {
      resetAutoSupplyTracker(store);
    }
    return;
  }

  // Сбрасываем трекер авто-снабжения, когда фаза снабжения закончилась,
  // чтобы при следующей фазе SUPPLY автодействия сработали снова.
  if (!dryRun && phase !== PHASES.SUPPLY) {
    resetAutoSupplyTracker(store);
  }

  // Одновременная фаза снабжения без игрока: авто-действия за каждого еще не
  // закончившего, в порядке хода (так их запускает сервер — в начале фазы и
  // после действий игроков; метка авто-раздачи у каждого своя). Сухому прогону
  // без игрока предсказывать нечего
  if (isParallelSupply && !actingPlayerId) {
    if (dryRun) {
      return;
    }
    getNotCompletedPlayers(sharedGameState).forEach((player) => {
      executePhaseAutoActions(store, isClientExecutor, hexGridRenderer, null, {actingPlayerId: player.id});
    });
    return;
  }

  if (!currentPlayer) {
    return;
  }
  // Игрок уже закончил свою часть одновременной фазы — действовать за него нечего
  if (isParallelSupply && (sharedGameState.phaseCompletedPlayers || []).includes(currentPlayer.id)) {
    return;
  }
  // Раздача за игрока в одновременной фазе несет его id (в поочередной фазе
  // снабжение раздает текущий игрок, и экшены остаются прежними)
  const supplyActorId = isParallelSupply ? currentPlayer.id : undefined;

  const units = selectUnits(state);
  const citiesCache = selectCitiesCache(state);
  const hexes = selectHexes(state);
  const pendingSupply = selectPendingSupply(state);
  const specialBuildings = selectSpecialBuildings(state);
  const players = selectPlayers(state);
  const pendingRecruitmentActions = selectPendingRecruitmentActions(state);
  const buildingsDamage = selectBuildingsDamage(state);
  const selectedFarmsForConversion = selectSelectedFarmsForConversion(state, currentPlayer.id);

  const playerCities = citiesCache?.[currentPlayer.color] || [];
  const currentPlayerCities = playerCities;
  const playerCitiesCount = playerCities.length;

  const playerFarms = [];
  const playerBarracks = [];
  for (const city of playerCities) {
    const farms = city[COMMON_BUILDING_TOKENS.FARM];
    if (farms) playerFarms.push(...farms);
    const barracks = city[COMMON_BUILDING_TOKENS.BARRACKS];
    if (barracks) playerBarracks.push(...barracks);
  }

  const playerUnits = units.filter(u => u.ownerId === currentPlayer.id);
  const hasNoUnits = playerUnits.length === 0;

  // Все точки автозавершения проходят через эти хелперы: в сухом прогоне
  // вместо реального завершения только фиксируется вердикт
  const completePhaseAuto = () => {
    if (dryRun) {
      dryRunResult.willComplete = true;
      return;
    }
    if (isParallelSupply) {
      completeParallelSupplyForPlayer(store, currentPlayer.id, {isClientExecutor});
      return;
    }
    executePhaseCompletion(store, isClientExecutor, false);
  };

  const completeWarStageAuto = () => {
    if (dryRun) {
      // Автозавершение этапа найма пропускает ВЕСЬ ход, только если после
      // применения pending-найма у игрока не останется отрядов (см.
      // executeWarPhaseStageCompletion) — иначе ход продолжится этапом
      // перемещения, и оверлей «Ваш ход» уместен
      dryRunResult.willComplete = hasNoUnits && pendingRecruitmentActions.length === 0;
      return;
    }
    executeWarPhaseStageCompletion(store, isClientExecutor, false);
  };

  // SUPPLY phase: auto-complete if player has no units or no working farms
  if (phase === PHASES.SUPPLY) {
    const isRatmenPlayer = isPlayerFaction(currentPlayer, FACTIONS.RATMEN);
    // Юнит-скоуп, а не факция игрока: соблазненный Великан сохраняет неограниченное
    // снабжение и под управлением игрока другой фракции (см. unit-scoped-faction-abilities).
    // ИЗВЕСТНОЕ ОГРАНИЧЕНИЕ: runGiantsAutoSupply ниже применяется КО ВСЕМ playerUnits
    // без разбора — для игрока-суккуба со СМЕШАННОЙ армией (захваченный Великан +
    // юниты других фракций) это ошибочно применит безлимитные правила Великанов и к
    // не-Великанам. Для игроков, изначально играющих за Великанов, это невозможно
    // (unitTypes фракции ограничен только d20), так что для них поведение не меняется
    const isGiantsPlayer = checkIfPlayerHasUnitOfFaction(currentPlayer, FACTIONS.GIANTS, units);

    const workingFarms = playerFarms.filter(farmHexKey => {
      return !checkIfHexBlockedByEnemyUnit(farmHexKey, TOKENS.FARM, currentPlayer.id, units, hexes, specialBuildings, buildingsDamage);
    });

    // Крысолюды: сначала раздаем снабжение с ферм, уже занятых отрядами, —
    // на них найм d6 за снабжение невозможен, а свободные фермы со снабжением
    // остаются доступными для найма
    if (isRatmenPlayer) {
      workingFarms.sort((farmA, farmB) => {
        const isFarmAOccupied = units.some(unit => unit.hexKey === farmA);
        const isFarmBOccupied = units.some(unit => unit.hexKey === farmB);
        return Number(isFarmBOccupied) - Number(isFarmAOccupied);
      });
    }

    // Крысолюды используют отдельное, более простое правило завершения хода
    // (см. конец блока, после отработки алгоритма распределения/найма) — не
    // участвуют в общих ранних авто-завершениях ниже. Для остальных фракций
    // все поведение (включая AGRARIANISM-паузу) не меняется
    const isPlayerPolicyAgrarianism = isPlayerPolicy(currentPlayer, POLICIES.AGRARIANISM);
    const ignoreAutoCompletion = isRatmenPlayer || (isPlayerPolicyAgrarianism && workingFarms.length > 1);

    if (!ignoreAutoCompletion && hasNoUnits) {
      completePhaseAuto();
      return;
    }

    const insectoidUnits = playerUnits.filter(isUnitInsectoid);
    if (!ignoreAutoCompletion && workingFarms.length === 0 && insectoidUnits.length === 0) {
      completePhaseAuto();
      return;
    }

    // Сухой прогон не читает и не пишет трекер: он всегда моделирует ПЕРВЫЙ
    // прогон авто-раздачи этой фазы, чтобы получить вердикт о завершении
    if (!dryRun) {
      const round = selectRound(state);
      if (shouldSkipSupplyAutoDistribution(store, currentPlayer, phase, round)) {
        return;
      }
      markSupplyAutoDistributed(store, currentPlayer, phase, round);
    }

    const supplyRenderer = hexGridRenderer || getGridGeometry(state);

    // Канонический порядок раздачи (живые тесты 13/14): (1) базовое по 1 каждому
    // достижимому существующему отряду — бюджеты занятых ферм раньше свободных,
    // (2) остатки — доливка лучшим до капа, (3) найм d6 крысолюдов на свободных
    // фермах с остатком (только бот-исполнитель, лимит одинаковых отрядов) —
    // ПОСЛЕДНИМ приоритетом, из бюджета, не разобранного доливками.
    // Приоритет отрядов для базового снабжения одинаков для всех фракций
    // (compareUnitsForBaseSupply), а для доливки различается: у крысолюдов
    // свой порядок типов (compareRatmenUnitsForAdditionalSupply), у остальных
    // — по размеру (compareUnitsForAdditionalSupply ниже).
    const UNIT_TYPE_PRIORITY = {
      'unit_d4': 0,
      'unit_d20': 1,
      'unit_d12': 2,
      'unit_d8': 3,
      'unit_d10': 4,
      'unit_d6': 5,
    };

    // Базовое снабжение (стадия 1): приоритет — ТИП (UNIT_TYPE_PRIORITY,
    // d4 > d20 > d12 > d8 > d10 > d6), размер — тайбрейк внутри типа
    const compareUnitsForBaseSupply = (a, b) => {
      const typePriorityDiff = (UNIT_TYPE_PRIORITY[a.type] ?? 99) - (UNIT_TYPE_PRIORITY[b.type] ?? 99);
      if (typePriorityDiff !== 0) {
        return typePriorityDiff;
      }
      return b.size - a.size;
    };

    // Дополнительное снабжение не-крысолюдов (стадия 2): наоборот, приоритет —
    // РАЗМЕР (по убыванию), тип (тот же UNIT_TYPE_PRIORITY) — тайбрейк внутри
    // размера
    const compareUnitsForAdditionalSupply = (a, b) => {
      if (a.size !== b.size) {
        return b.size - a.size;
      }
      return (UNIT_TYPE_PRIORITY[a.type] ?? 99) - (UNIT_TYPE_PRIORITY[b.type] ?? 99);
    };

    let allFarmsHaveZeroOrSingleTarget = true;
    const autoSupplies = [];
    const allAvailableCellsForFarmsWithMoreThenOneTarget = new Set();

    // Авто-раздача обязана быть идемпотентной: в одном ходе она может
    // запуститься повторно поверх собственного результата, и трекер выше этого
    // не ловит. Он привязан к стору (или autoActionsTrackerKey), а ход бота
    // раздается дважды: сервером в начале хода и затем из decideSupplyStep —
    // у делегата это вообще другой стор (клиентский), у серверного fallback —
    // свежая обертка executeBotStep. Сухой прогон клиента тоже может застать
    // серверную раздачу уже в сторе. Поэтому счетчик
    // снабжения отрядов засевается уже розданным в этот ход, а не стартует с
    // нуля: иначе повторная выдача тому же отряду превращается редьюсером в
    // дополнительное снабжение (реплей i2dlkf4)
    const pendingSupplyByUnit = {};
    pendingSupply.forEach(supply => {
      pendingSupplyByUnit[supply.unitId] = (pendingSupplyByUnit[supply.unitId] || 0) + 1;
    });

    insectoidUnits.forEach(unit => {
      const hasMockFarmSupply = pendingSupply.some(
        supply => supply.unitId === unit.id && supply.farmHexKey === FACTION_INSECTOIDS_MOCK_FARM_HEX_KEY
      );
      if (hasMockFarmSupply) {
        return;
      }
      autoSupplies.push({
        farmHexKey: FACTION_INSECTOIDS_MOCK_FARM_HEX_KEY,
        unitId: unit.id,
        unitHexKey: unit.hexKey,
        isNotUndoable: true
      });
      pendingSupplyByUnit[unit.id] = (pendingSupplyByUnit[unit.id] || 0) + 1;
    });

    // Бюджеты и достижимые отряды каждой работающей фермы.
    // Кто из отрядов спорный (ферма на 2+ отряда) — решает емкость фермы на
    // ход, а не остаток: иначе на повторном прогоне ферма, уже целиком
    // розданная первым, выпадала бы из проверки, и ход игрока, у которого
    // остался выбор, признавался бы однозначным (автозавершение ниже). Бюджет
    // раздачи — по-прежнему остаток; ферма с нулевым остатком только
    // участвует в проверке спорности (все стадии пропускают plan.budget <= 0)
    const farmPlans = [];
    for (const farmHexKey of workingFarms) {
      const turnSupplyCapacity = calcFarmRemainingSupply(
        farmHexKey, selectedFarmsForConversion, hexes, specialBuildings, players, units, citiesCache, currentPlayer, [], buildingsDamage
      );
      if (turnSupplyCapacity <= 0) {
        continue;
      }
      const remainingSupply = calcFarmRemainingSupply(
        farmHexKey, selectedFarmsForConversion, hexes, specialBuildings, players, units, citiesCache, currentPlayer, pendingSupply, buildingsDamage
      );
      const availableCells = calculateAvailableSupplyCells(
        supplyRenderer,
        hexes,
        specialBuildings,
        units,
        farmHexKey,
        currentPlayer,
        currentPlayerCities || [],
        buildingsDamage,
      );
      const targetUnits = availableCells
        .map(hexKey => playerUnits.find(u => u.hexKey === hexKey))
        .filter(Boolean)
        .sort(compareUnitsForBaseSupply);

      if (targetUnits.length >= 2) {
        allFarmsHaveZeroOrSingleTarget = false;
        targetUnits.forEach(u => allAvailableCellsForFarmsWithMoreThenOneTarget.add(u.id));
      }

      farmPlans.push({
        farmHexKey,
        budget: remainingSupply,
        targetUnits,
        isOccupied: units.some(unit => unit.hexKey === farmHexKey),
      });
    }

    const assignSupply = (plan, unit) => {
      pendingSupplyByUnit[unit.id] = (pendingSupplyByUnit[unit.id] || 0) + 1;
      plan.budget -= 1;
      autoSupplies.push({farmHexKey: plan.farmHexKey, unitId: unit.id, unitHexKey: unit.hexKey});
    };

    // Диспатчим накопленное в autoSupplies и очищаем очередь — вызывается
    // между стадиями, чтобы порядок событий DISTRIBUTE_SUPPLY в сторе (а
    // значит и порядок отмены UndoButton-ом, который снимает последнее
    // событие) совпадал с приоритетом вложений снабжения: базовое (стадия 1),
    // доливки существующим (стадия 2), найм d6 крысолюдов (стадия 3 — самое
    // низкоприоритетное вложение, и планируется, и диспатчится ПОСЛЕ доливок),
    // доливка свежему отряду (стадия 4 — в самом конце: она ссылается на
    // нанятый отряд и должна отменяться раньше него)
    const flushAutoSupplies = () => {
      const suppliesToDispatch = autoSupplies.splice(0, autoSupplies.length);
      if (dryRun) {
        return;
      }
      for (const supply of suppliesToDispatch) {
        store.dispatch(distributeSupply(supply.farmHexKey, supply.unitId, supply.unitHexKey, true, supply.isNotUndoable, undefined, supplyActorId));
      }
    };

    // Фермы с ровно ОДНИМ достижимым отрядом — в первую очередь: иначе такой
    // отряд успевают докормить/забить до капа другие (многоцелевые) фермы
    // раньше, чем очередь дойдет до единственной фермы, которая вообще может
    // его снабдить, — и ее бюджет уходит впустую (отдать ей больше некому).
    // Внутри каждой группы — бюджеты занятых ферм раньше свободных: на
    // занятых найм невозможен, а свободные фермы с остатком остаются под
    // найм d6 крысолюдов
    farmPlans.sort((planA, planB) => {
      const isPlanASingleTarget = planA.targetUnits.length === 1;
      const isPlanBSingleTarget = planB.targetUnits.length === 1;
      if (isPlanASingleTarget !== isPlanBSingleTarget) {
        return isPlanASingleTarget ? -1 : 1;
      }
      return Number(planB.isOccupied) - Number(planA.isOccupied);
    });

    // Великаны: отдельный, упрощенный алгоритм (не участвует в стадиях 1-5 ниже,
    // рассчитанных на остальные фракции) - см. runGiantsAutoSupply
    if (isGiantsPlayer) {
      const {hasUnresolvedChoice} = runGiantsAutoSupply(
        store, dryRun, farmPlans, playerUnits, pendingSupply, Boolean(currentPlayer.isGiantsAutoAdditionalSupplyOn), supplyActorId
      );

      // Авто-раздача только ускоряет распределение снабжения - финальное решение
      // (закончить фазу) остается за игроком всегда, когда у него был выбор:
      // независимо от положения тумблера и от того, что именно раздал алгоритм
      if (!ignoreAutoCompletion && !hasUnresolvedChoice) {
        completePhaseAuto();
      }

      return;
    }

    // Стадия 1: базовое (по 1) каждому достижимому существующему отряду —
    // гарантия пайка до чьей-либо доливки (отряд без снабжения теряет размер,
    // а доливка растит размер только d6 крысолюдов; регрессия отчета №8)
    for (const plan of farmPlans) {
      if (plan.budget <= 0) continue;
      for (const unit of plan.targetUnits) {
        if (plan.budget <= 0) break;
        if ((pendingSupplyByUnit[unit.id] || 0) >= 1) continue;
        assignSupply(plan, unit);
      }
    }
    flushAutoSupplies();

    // Стадия 2: остатки — доливка (дополнительное снабжение) существующим
    // отрядам до капа. У крысолюдов — в своем порядке приоритета ТИПОВ
    // (d20 → d10 → d6 → d4 → d12 → d8, см. compareRatmenUnitsForAdditionalSupply),
    // у остальных фракций — по РАЗМЕРУ (тип — тайбрейк, см.
    // compareUnitsForAdditionalSupply) — намеренно обратный порядку стадии 1.
    // Доливки планируются РАНЬШЕ найма: найм d6 крысолюдов — последний
    // приоритет и получает только бюджет, не разобранный доливками
    for (const plan of farmPlans) {
      if (plan.budget <= 0) continue;
      const unitsInAdditionalSupplyOrder = isRatmenPlayer
        ? [...plan.targetUnits].sort(compareRatmenUnitsForAdditionalSupply)
        : [...plan.targetUnits].sort(compareUnitsForAdditionalSupply);
      for (const unit of unitsInAdditionalSupplyOrder) {
        while (plan.budget > 0 && (pendingSupplyByUnit[unit.id] || 0) < MAX_UNIT_SUPPLY) {
          assignSupply(plan, unit);
        }
        if (plan.budget <= 0) break;
      }
    }
    // Доливки существующим отрядам — в стор до найма (порядок отмены)
    flushAutoSupplies();

    // Стадия 3: крысолюды автоматически нанимают d6 за снабжение на СВОБОДНЫХ
    // фермах с остатком — ПОСЛЕДНИЙ приоритет вложения снабжения: существующие
    // отряды уже накормлены (стадия 1) и долиты до капа (стадия 2), найму
    // достается только оставшийся после них бюджет. recruitedThisPass кормит
    // стадию 4 — свежий отряд не попадает ни в pendingSupplyByUnit, ни в
    // plan.targetUnits (тот посчитан раньше), поэтому без этого списка
    // стадия 4 его в принципе не видит
    const recruitedThisPass = [];
    // В последнем раунде новые отряды не нанимаются вовсе — ни игроком (кнопка
    // найма на ферме отвечает тостом, см. RatmenRecruitmentToolbar), ни этой
    // автоматикой. Бюджет свободных ферм в этом случае просто остается
    // неизрасходованным: существующие отряды уже долиты до капа стадией 2
    if (isRatmenPlayer && !checkIfFinalRound(state)) {
      const unitsWithRecruits = [...playerUnits];
      for (const plan of farmPlans) {
        if (plan.budget <= 0 || plan.isOccupied) continue;
        if (!checkSameTypeUnitRecruitmentLimit(currentPlayer, UNIT_TYPES.D6.id, unitsWithRecruits)) {
          break;
        }
        if (!dryRun) {
          const newUnitId = createRandomId(ID_PREFIX_FOR_UNIT);
          pendingSupplyByUnit[newUnitId] = 1;
          recruitedThisPass.push({id: newUnitId, hexKey: plan.farmHexKey, plan});
        }
        plan.budget -= 1;
        unitsWithRecruits.push({type: UNIT_TYPES.D6.id});
      }
    }

    // Диспатч найма стадии 3: после доливок (UndoButton снимает последний
    // экшен pendingSupply — найм откатывается раньше доливок), но ДО стадии 4
    // — ее доливка свежему отряду ссылается на только что нанятый отряд и
    // должна отменяться раньше него
    if (isRatmenPlayer && !dryRun) {
      for (const recruit of recruitedThisPass) {
        store.dispatch(recruitUnit(recruit.plan.farmHexKey, UNIT_TYPES.D6.id, FACTIONS.RATMEN, 0, recruit.id, true, supplyActorId));
        store.dispatch(distributeSupply(recruit.plan.farmHexKey, recruit.id, recruit.plan.farmHexKey, false, false, true, supplyActorId));
      }
    }

    // Стадия 4 (крысолюды): остаток бюджета фермы, нанявшей отряд в стадии 3,
    // — этому же отряду (доливка свежему d6). После доливок всем существующим
    // (стадия 2) и самого найма — иначе такой остаток просто терялся (свежий
    // отряд не участвовал в стадии 2, см. комментарий выше)
    if (isRatmenPlayer) {
      for (const recruit of recruitedThisPass) {
        while (recruit.plan.budget > 0 && (pendingSupplyByUnit[recruit.id] || 0) < MAX_UNIT_SUPPLY) {
          if (globalThis.__stage4DebugProbe) globalThis.__stage4DebugProbe(recruit.id);
          assignSupply(recruit.plan, recruit);
        }
      }
    }

    // Стадия 5 (крысолюды): остаток бюджета ЛЮБОЙ фермы (в т.ч. не нанимавшей
    // никого в стадии 3 — например, занятой, или свободной, но упершейся в
    // лимит одинаковых отрядов) — недокормленным свежим новобранцам этого
    // хода, если до их клетки есть валидный путь снабжения с фермы-донора.
    // На момент построения farmPlans новобранцев еще не существовало, поэтому
    // достижимость считаем заново с units, дополненным recruitedThisPass.
    // Донор пропускает собственного новобранца — тот уже разобран стадией 4.
    // Самый низкий приоритет вложения снабжения: только то, что не пригодилось
    // нигде раньше
    if (isRatmenPlayer && recruitedThisPass.length > 0) {
      const unitsWithNewRecruits = [
        ...units,
        ...recruitedThisPass.map(recruit => ({
          id: recruit.id,
          ownerId: currentPlayer.id,
          hexKey: recruit.hexKey,
          type: UNIT_TYPES.D6.id,
        })),
      ];

      for (const donorPlan of farmPlans) {
        if (donorPlan.budget <= 0) continue;

        const reachableFromDonor = new Set(calculateAvailableSupplyCells(
          supplyRenderer, hexes, specialBuildings, unitsWithNewRecruits, donorPlan.farmHexKey,
          currentPlayer, currentPlayerCities || [], buildingsDamage,
        ));

        for (const recruit of recruitedThisPass) {
          if (donorPlan.budget <= 0) break;
          if (donorPlan === recruit.plan) continue;
          if (!reachableFromDonor.has(recruit.hexKey)) continue;
          while (donorPlan.budget > 0 && (pendingSupplyByUnit[recruit.id] || 0) < MAX_UNIT_SUPPLY) {
            assignSupply(donorPlan, recruit);
          }
        }
      }
    }

    if (!dryRun && globalThis.__supplyProbe && workingFarms.length > 0 && playerUnits.length > 0) {
      globalThis.__supplyProbe(
        currentPlayer.id,
        playerUnits.filter(u => !(pendingSupplyByUnit[u.id] > 0)).length,
        playerUnits.length
      );
    }

    flushAutoSupplies();

    if (isRatmenPlayer) {
      // Крысолюды: бот — всегда сразу после отработки алгоритма (весь набор
      // решений, включая найм, уже принят детерминированно); реальный игрок —
      // только если у него меньше 2 работающих ферм (иначе распределение/наём
      // на нескольких фермах — его осознанный выбор)
      // В одновременной фазе бот так не спешит: его часть фазы завершает
      // серверный планировщик с человеческой паузой — иначе галочка бота
      // появлялась бы мгновенно и выдавала его
      if ((currentPlayer.isBot && !isParallelSupply) || workingFarms.length < 2) {
        completePhaseAuto();
      }
    } else {
      let allMultiTargetUnitsFullySupplied = true;
      for (const unitId of allAvailableCellsForFarmsWithMoreThenOneTarget) {
        if ((pendingSupplyByUnit[unitId] || 0) < MAX_UNIT_SUPPLY) {
          allMultiTargetUnitsFullySupplied = false;
          break;
        }
      }

      if (!ignoreAutoCompletion
        && (allMultiTargetUnitsFullySupplied || allFarmsHaveZeroOrSingleTarget)
      ) {
        completePhaseAuto();
      }
    }

    return;
  }

  // CONSTRUCTION phase: auto-complete if player has no cities
  if (phase === PHASES.CONSTRUCTION) {
    if (playerCitiesCount === 0) {
      completePhaseAuto();
    }
    return;
  }

  // WAR phase: handle different stages
  if (phase === PHASES.WAR && warPhaseStage === WAR_PHASE_STAGES.RECRUITMENT) {
    // Свои отряды на РАБОЧИХ казармах: им на этапе можно улучшить тип и
    // докупить размер, а в последнем раунде только эти действия и остаются
    const unitsOnWorkingBarracks = playerBarracks.reduce((acc, barracksHexKey) => {
      if (checkIfHexBlockedByEnemyUnit(
        barracksHexKey, TOKENS.BARRACKS, currentPlayer.id, units, hexes, specialBuildings, buildingsDamage
      )) {
        return acc;
      }
      const unitOnBarracks = playerUnits.find(unit => unit.hexKey === barracksHexKey);
      if (unitOnBarracks) {
        acc.push(unitOnBarracks);
      }
      return acc;
    }, []);
    // Из них — с окном докупки размера (size < maxSize у самого отряда -
    // фактический максимум, у великанов увеличен относительно статического
    // конфига типа, см. createUnit). Докупка размера в пороге монет
    // calcRecruitmentStageMinCoins возможна, только если такой отряд есть
    const sizeUpgradableUnits = unitsOnWorkingBarracks.filter(unit => unit.size < unit.maxSize);

    // Последний раунд: НОВЫЕ отряды не нанимаются (сходить они не успеют), но
    // стоящим на рабочих казармах можно улучшить тип и докупить размер. Нет
    // таких отрядов — делать на этапе нечего. Есть — дальше те же условия по
    // монетам, что и в других раундах, только минимальное действие считается
    // без найма новых отрядов
    const isFinalRound = checkIfFinalRound(state);
    if (isFinalRound && unitsOnWorkingBarracks.length === 0) {
      completeWarStageAuto();
      return;
    }

    const hasWorkingBarracks = playerBarracks.some(barracksHexKey => {
      return !checkIfHexBlockedByEnemyUnit(barracksHexKey, TOKENS.BARRACKS, currentPlayer.id, units, hexes, specialBuildings, buildingsDamage);
    });

    const minActionCoins = calcRecruitmentStageMinCoins(currentPlayer, {
      sizeUpgradableUnits,
      upgradeCandidates: unitsOnWorkingBarracks,
      units,
      canRecruitNewUnits: !isFinalRound,
    });

    if (!hasWorkingBarracks ||
      (pendingRecruitmentActions.length === 0 && currentPlayer.coins < minActionCoins)
    ) {
      if (hasWorkingBarracks) {
        // Варварство (бесплатный d6) и деспотизм (скидка на найм) удешевляют
        // только НОВЫЕ отряды — в последнем раунде их не нанимают, и держать
        // этап ради них незачем. Скидка патриотизма на докупку размера там уже
        // учтена в минимуме: докупка считается по цене со скидкой, а без отряда
        // с окном докупки скидке не на что пойти
        if (!isFinalRound && isPlayerPolicy(currentPlayer, POLICIES.BARBARISM)) {
          return;
        }
        if (!isFinalRound
          && currentPlayer.coins === 1
          && (isPlayerPolicy(currentPlayer, POLICIES.PATRIOTISM)
            || isPlayerPolicy(currentPlayer, POLICIES.DESPOTISM))
        ) {
          return;
        }
        // Обмен победных очков на монеты может дать денег на действие — этап
        // держим, только если после обмена его и правда хватит (действия нет
        // вовсе — минимум бесконечен, и обмен тоже не поможет)
        if (isPlayerPolicy(currentPlayer, POLICIES.FANATICISM)
          && !currentPlayer.isFanaticismConversionUsed
          && currentPlayer.winPoints >= POLICY_FANATICISM_WIN_POINTS_CONVERSION
          && currentPlayer.coins + POLICY_FANATICISM_COINS_CONVERSION >= minActionCoins
        ) {
          return;
        }
      }

      completeWarStageAuto();
    }
  }
};
